import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { logError, LogCategory } from "utils/sentryLogger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Input } from "@bootstrap-styled/v4";
import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { colors } from "utils";
import { ActionButton } from "./ActionButton";
import { FlagBugIcon } from "./icons/FlagBugIcon";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useSessionContext } from "hooks/useSession";
import { useDataTrack } from "hooks/livekit/useDataTrack/useDataTrack";
import { useClientInfo } from "hooks/useClientInfo";

export const FeedbackButton = ({ style, reportLocation }) => {
  const { session } = useSessionContext();
  const clientInfo = useClientInfo();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailsText, setDetailsText] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [challengeId, setChallengeId] = useState();
  const [orchestratorState, setOrchestratorState] = useState();

  useDataTrack((e) => {
    // student_rejoin carries a full state_change payload in data.state.
    const payload =
      e?.event_type === "state_change" ? e.data
      : e?.event_type === "student_rejoin" ? e.data?.state
      : null;
    if (!payload) return;
    if (payload.state) setOrchestratorState(payload.state);
    // most_recent_challenge_context is on every state_change, so a late joiner
    // who never saw the challenge state still gets the question id.
    const id = payload.most_recent_challenge_context?.challenge?.id;
    if (id) setChallengeId(id);
  });

  const getInternetSpeed = () => {
    const connection = navigator.connection;
    if (!connection?.downlink) return null;
    return `${connection.downlink} Mbps`;
  };

  const handleSubmit = async () => {
    if (!detailsText) return;
    setIsLoading(true);
    await request("feedback/submit_custom_feedback", {
      payload: {
        feedback_type: "bug",
        details: detailsText,
        session_id: session?.id ?? null,
        challenge_id: challengeId ?? null,
        orchestrator_state: orchestratorState ?? null,
        report_location: reportLocation,
        browser_version: clientInfo.summary,
        internet_speed: getInternetSpeed(),
      },
    }).then(() => {
      setIsModalOpen(false);
      setDetailsText(null);
    }).catch((e) => {
      console.error(e);
      logError("Failed to submit feedback", e, {
        category: LogCategory.SESSION,
        sessionId: session?.id,
      });
    }).finally(()=> setIsLoading(false));
  };

  const handleClose = () => {
    if (isLoading) return;
    setIsModalOpen(false);
  };

  return (
    <>
      <ActionButton
        onClick={() => setIsModalOpen(true)}
        style={style}
        ariaLabel="Report issue or provide feedback"
        tooltip={"Report issue / provide feedback"}
      >
        <FlagBugIcon />
      </ActionButton>
      <Modal show={isModalOpen} aria-labelledby="feedback-modal-title">
        {isLoading && <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999
        }}>
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            size="5x"
          />
        </div>}
        <Modal.Header><span id="feedback-modal-title" style={{ fontSize: "20px" }}>Provide feedback or report an issue</span></Modal.Header>
        <Modal.Body>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Input placeholder="Please provide details here..."
                   aria-label="Feedback details"
                   maxLength={500}
                   type="textarea"
                   style={{ height: "100px" }}
                   onChange={(e) => setDetailsText(e.target.value)}
                   value={detailsText} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleSubmit}
            style={{
              backgroundColor: colors.pink500,
              border: "none",
              borderRadius: "20px",
              fontSize: "16px",
              cursor: isLoading || !detailsText ? "not-allowed" : "pointer",
              opacity: isLoading || !detailsText ? 0.5 : 1
            }}
          >
            Submit
          </Button>
          <Button
            onClick={handleClose}
            style={{
              backgroundColor: "white",
              border: `1px solid ${colors.nightlyWoods500}`,
              borderRadius: "20px",
              color: colors.nightlyWoods500,
              fontSize: "16px",
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
